import {
  Address,
  addressTemplate,
  addressTextTemplate,
  applyReportParametersToURLSearchParams,
  Choice,
  currencyFormat,
  emailTemplate,
  html,
  isValidURLResponse,
  limitConcurrent,
  numberFormat,
  parseFormData,
  Phone,
  phoneTemplate,
  render,
  reportGridColumnDefTypes,
  reportLimits,
  ReportParameterValues,
  ReportRowsLoadedPayload,
  tryFormat24HourTime,
  uuidToBase58,
  userDateFormat
} from '@cumu/shared';
import { defaultLocale, loc_no, loc_open, loc_yes } from '@cumu/strings';
import { controller, target } from '@github/catalyst';
import type AgGrid from 'ag-grid-enterprise';
import type { FormBehaviorElement } from '../forms/behavior-element';
import { getLocale } from '../locale';
import { documentVisible, elementVisible } from '../visibility';

declare const AG_GRID_LICENSE_KEY: string;
declare const agGrid: typeof AgGrid;

const locale = getLocale();

const limit = limitConcurrent(2);

const datetimeFormat = new Intl.DateTimeFormat(defaultLocale, {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hour12: true
});

function reportLinkValue(value: unknown, colDef: AgGrid.ColDef | undefined) {
  const base58UuidLinkPath = colDef?.context?.base58_uuid_link_path;
  if (typeof value !== 'string') {
    return '';
  }
  if (typeof base58UuidLinkPath !== 'string') {
    return value;
  }
  try {
    return new URL(
      `${base58UuidLinkPath}${uuidToBase58(value)}`,
      location.origin
    ).href;
  } catch {
    return '';
  }
}

const columnTypes: Record<string, AgGrid.ColTypeDef> = {
  [reportGridColumnDefTypes.single_choice]: {
    valueFormatter: ({ value, colDef }) => {
      if (typeof value === 'number') {
      } else if (typeof value === 'boolean') {
        value = value ? 1 : 0;
      } else {
        return value;
      }
      const choices: Choice[] | undefined = colDef?.context?.choices;
      return choices?.find(c => c.choice_id === value)?.label;
    }
  },
  [reportGridColumnDefTypes.multiple_choice]: {
    valueFormatter: ({ value, colDef }) => {
      if (typeof value !== 'number') {
        return value;
      }
      const choices: Choice[] | undefined = colDef?.context?.choices;
      return choices
        ?.filter(c => ((2 ** c.choice_id) & value) !== 0)
        .map(c => c.label)
        .join(', ');
    }
  },
  [reportGridColumnDefTypes.boolean]: {
    valueFormatter: ({ value }) => {
      if (typeof value !== 'boolean' && value !== 0 && value !== 1) {
        return value;
      }
      return value ? loc_yes[locale] : loc_no[locale];
    }
  },
  [reportGridColumnDefTypes.date]: {
    valueFormatter: ({ value }) => {
      if (typeof value !== 'string' || value === '') {
        return value;
      }
      if (value.length === 10) {
        const ds = value.split('-').map(s => parseInt(s, 10));
        ds[1] = ds[1] - 1; // js month is zero-based month
        const d = new Date(ds[0], ds[1], ds[2]);
        return userDateFormat.format(d);
      }
      const d = new Date(value + (value.endsWith('Z') ? '' : 'Z'));
      return datetimeFormat.format(d);
    }
  },
  [reportGridColumnDefTypes.decimal]: {
    valueFormatter: ({ value }) => {
      if (typeof value !== 'number') {
        return value;
      }
      return numberFormat.format(value);
    }
  },
  [reportGridColumnDefTypes.currency]: {
    valueFormatter: ({ value }) => {
      if (typeof value !== 'number') {
        return value;
      }
      return currencyFormat.format(value);
    }
  },
  [reportGridColumnDefTypes.int]: {
    valueFormatter: ({ value }) => {
      if (typeof value !== 'number') {
        return value;
      }
      // hack: don't use commas when the int might be a year
      if (value >= 1900 || value < 2100) {
        return value;
      }
      return numberFormat.format(value);
    }
  },
  [reportGridColumnDefTypes.row_href]: {
    cellRenderer: ({ value, colDef }: AgGrid.ICellRendererParams) => {
      const a = document.createElement('a');
      a.className = 'btn btn-sm';
      a.href = value;
      a.textContent = colDef?.cellRendererParams?.title ?? loc_open[locale];
      a.style.marginTop = '-6px';
      return a;
    },
    valueFormatter: ({ value }) => {
      return location.origin + value;
    }
  },
  [reportGridColumnDefTypes.link]: {
    cellRenderer: ({ value, colDef }: AgGrid.ICellRendererParams) => {
      value = reportLinkValue(value, colDef);
      if (isValidURLResponse({ value, format: colDef!.context.link_format })) {
        const a = document.createElement('a');
        a.href = value;
        a.textContent = colDef?.headerName ?? value;
        return a;
      }
      return '';
    },
    valueFormatter: ({ value, colDef }) => reportLinkValue(value, colDef)
  },
  [reportGridColumnDefTypes.time]: {
    cellRenderer: ({ value }: AgGrid.ICellRendererParams) => {
      if (typeof value === 'string' && value.length) {
        value = tryFormat24HourTime(value).value;
        const time = document.createElement('time');
        time.dateTime = value;
        time.textContent = value;
        return time;
      }
      return '';
    }
  },
  [reportGridColumnDefTypes.composite_address]: {
    cellRenderer: ({ colDef, data: row }: AgGrid.ICellRendererParams) => {
      const map = colDef!.context.composite_address_map;
      const address: Partial<Address> = {
        street_address: row[map['street_address']],
        sub_address: row[map['sub_address']],
        city: row[map['city']],
        region_abbr: row[map['region_abbr']],
        subregion: row[map['subregion']],
        postal_code: row[map['postal_code']],
        postal_code_ext: row[map['postal_code_ext']]
      };
      return render(addressTemplate(address, true));
    },
    valueGetter: ({ colDef, data: row }) => {
      const map = colDef!.context.composite_address_map;
      const address: Partial<Address> = {
        street_address: row[map['street_address']],
        sub_address: row[map['sub_address']],
        city: row[map['city']],
        region_abbr: row[map['region_abbr']],
        subregion: row[map['subregion']],
        postal_code: row[map['postal_code']],
        postal_code_ext: row[map['postal_code_ext']]
      };
      return addressTextTemplate(address);
    }
  },
  [reportGridColumnDefTypes.composite_phone]: {
    cellRenderer: ({ colDef, data: row }: AgGrid.ICellRendererParams) => {
      const map = colDef!.context.composite_phone_map;
      const phone: Pick<Phone, 'type' | 'number' | 'extension'> = {
        type: row[map['type']],
        number: row[map['number']],
        extension: row[map['extension']]
      };
      if (phone.type && phone.number) {
        return render(phoneTemplate(phone, true));
      }
      return '';
    },
    valueGetter: ({ colDef, data: row }) => {
      const map = colDef!.context.composite_phone_map;
      const phone: Pick<Phone, 'type' | 'number' | 'extension'> = {
        type: row[map['type']],
        number: row[map['number']],
        extension: row[map['extension']]
      };
      if (phone.type && phone.number) {
        return phoneTemplate(phone, false);
      }
      return '';
    }
  },
  [reportGridColumnDefTypes.composite_email]: {
    cellRenderer: ({ colDef, data: row }: AgGrid.ICellRendererParams) => {
      const map = colDef!.context.composite_email_map;
      const email = {
        type: row[map['type'] ?? 'zzz-nothing-matches-this-key'],
        email: row[map['email']]
      };
      if (email.type && email.email) {
        return render(emailTemplate(email, true, false));
      }
      if (email.email) {
        const a = document.createElement('a');
        a.href = `mailto:${email.email}`;
        a.className = 'Link--primary';
        a.textContent = email.email;
        return a;
      }
      return '';
    },
    valueGetter: ({ colDef, data: row }) => {
      const map = colDef!.context.composite_email_map;
      const email = {
        type: row[map['type'] ?? 'zzz-nothing-matches-this-key'],
        email: row[map['email']]
      };
      if (email.type && email.email) {
        return `${email.type}: ${email.email}`;
      }
      if (email.email) {
        return email.email;
      }
      return '';
    }
  },
  [reportGridColumnDefTypes.value_map]: {
    valueFormatter: ({ value, colDef }) => {
      const map = colDef!.context.value_map;
      return map[value] ?? value;
    }
  }
};

@controller('report-grid')
class ReportGridElement extends HTMLElement {
  @target declare optionsScript: HTMLScriptElement;
  @target declare keyScript: HTMLScriptElement;
  @target declare gridContainer: HTMLDivElement;
  @target declare parametersForm: HTMLFormElement;
  grid: AgGrid.GridApi | undefined;
  declare key: string;

  connectedCallback() {
    agGrid.LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);
    if (this.closest('[hidden]')) {
      return;
    }
    this.initialize();
  }

  disconnectedCallback() {
    this.grid?.destroy();
  }

  initialize() {
    if (this.grid) {
      return;
    }
    const initialVisiblePromise = documentVisible().then(() =>
      elementVisible(this)
    );
    const options: AgGrid.GridOptions = JSON.parse(
      this.optionsScript.textContent!
    );
    this.key = JSON.parse(this.keyScript.textContent!);
    this.optionsScript.remove();
    options.getContextMenuItems = () => {
      return ['copy'];
    };
    options.cellSelection = true;
    options.popupParent = document.body;
    options.columnTypes = columnTypes;
    options.rowModelType = 'serverSide';
    options.skipHeaderOnAutoSize = true;
    options.suppressColumnVirtualisation = true;
    options.rowClassRules = {
      'color-bg-subtle': ({ rowIndex }) => rowIndex % 2 !== 0
    };
    options.defaultColDef = Object.assign(options.defaultColDef ?? {}, {
      headerComponentParams: {
        template: render(
          html`<div class="ag-cell-label-container" role="presentation">
            <span
              data-ref="eMenu"
              class="ag-header-icon ag-header-cell-menu-button"
              aria-hidden="true"
            ></span>
            <span
              data-ref="eFilterButton"
              class="ag-header-icon ag-header-cell-filter-button"
              aria-hidden="true"
            ></span>
            <div
              data-ref="eLabel"
              class="ag-header-cell-label"
              role="presentation"
            >
              <span
                data-ref="eText"
                class="ag-header-cell-text line-clamp-4"
              ></span>
              <span
                data-ref="eFilter"
                class="ag-header-icon ag-header-label-icon ag-filter-icon"
                aria-hidden="true"
              ></span>
              <span
                data-ref="eSortAsc"
                class="ag-header-icon ag-header-label-icon ag-sort-ascending-icon"
                style="--ag-icon-size: 14px"
                aria-hidden="true"
              ></span>
              <span
                data-ref="eSortDesc"
                class="ag-header-icon ag-header-label-icon ag-sort-descending-icon"
                style="--ag-icon-size: 14px"
                aria-hidden="true"
              ></span>
              <!-- <span
                data-ref="eSortNone"
                class="ag-header-icon ag-header-label-icon ag-sort-none-icon"
                style="--ag-icon-size: 14px"
                aria-hidden="true"
              ></span> -->
              <span
                data-ref="eSortOrder"
                class="ag-sort-order"
                style="position: relative; bottom: -8px; left: -4px; font-size: 0.625rem"
                aria-hidden="true"
              ></span>
            </div>
          </div>`
        )
      }
    });
    options.serverSideDatasource = {
      getRows: async ({
        request: { startRow, endRow, sortModel },
        success,
        fail
      }) => {
        await initialVisiblePromise;
        await limit(async () => {
          const url = this.getAttribute('row-source')!;
          const { parameter_values = {} } = parseFormData<{
            parameter_values?: ReportParameterValues;
          }>(new FormData(this.parametersForm));
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              startRow,
              endRow,
              sortModel: sortModel.slice(0, reportLimits.sorts),
              key: this.key,
              parameter_values
            })
          });
          if (!response.ok) {
            fail();
            return;
          }
          const detail: ReportRowsLoadedPayload = await response.json();
          success({ rowData: detail.rows, rowCount: detail.count });
          this.dispatchEvent(
            new CustomEvent<ReportRowsLoadedPayload>(
              'report-grid-rows-loaded',
              {
                detail,
                bubbles: true
              }
            )
          );
        });
      }
    };
    const grid = (this.grid = agGrid.createGrid(this.gridContainer, options));
    grid.addEventListener('firstDataRendered', () => {
      setTimeout(() => {
        const columns = grid.getAllDisplayedColumns();
        const widths = columns.map(c => c.getActualWidth());
        const columnsWidth = widths.reduce((a, b) => a + b, 0);
        const gridWidth = this.clientWidth;
        if (columnsWidth > gridWidth - 16) {
          return;
        }
        const available = gridWidth - columnsWidth;
        const start = columns[0].getColId() === 'navigation' ? 1 : 0;
        const utilized = widths.slice(start).reduce((a, b) => a + b, 0);
        const newWidths: { key: string; newWidth: number }[] = [];
        for (let i = start; i < columns.length; i++) {
          const current = widths[i];
          const ratio = current / utilized;
          const newWidth = Math.min(
            current + available * ratio,
            Math.max(current, 500)
          );
          newWidths.push({ key: columns[i].getColId(), newWidth });
        }
        grid.setColumnWidths(newWidths);
      });
    });
  }

  submitParameters(event?: CustomEvent<FormData>) {
    event?.preventDefault();
    (event?.target as FormBehaviorElement | undefined)?.acceptChanges();

    if (event?.detail.get('download-csv') === 'true') {
      event.detail.delete('download-csv');
      const action = this.getAttribute('csv-action')!;
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = action;
      form.style.display = 'none';
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'key';
      input.value = this.key;
      form.appendChild(input);
      for (const [key, value] of event.detail.entries()) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value as string;
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
      form.remove();
      return;
    }

    if (this.grid) {
      this.grid.refreshServerSide({ purge: true });
      if (event) {
        const { parameter_values = {} } = parseFormData<{
          parameter_values?: ReportParameterValues;
        }>(new FormData(this.parametersForm));
        const locationUrl = new URL(location.href);
        applyReportParametersToURLSearchParams(
          locationUrl.searchParams,
          parameter_values
        );
        history.replaceState(null, '', locationUrl.href);
      }
    } else {
      this.initialize();
    }
  }
}

declare global {
  interface Window {
    ReportGridElement: typeof ReportGridElement;
  }
  interface HTMLElementTagNameMap {
    'report-grid': ReportGridElement;
  }
}
